import { api } from '@goaltree/backend/convex/_generated/api'
import type { GoalIdeaFact } from '@goaltree/backend/convex/lib/goalIdeas'
import { IMPROVE_AREAS, improveLang } from '@goaltree/backend/convex/lib/improveAreas'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { ArrowRight, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { CATEGORY_COLORS, categoryTextColor } from '@/lib/category-color'
import { useOnboardingStore } from '@/store/onboarding.store'

const PILLS = ['goal', 'questions', 'plan'] as const

export function GoalStepPills({ className }: { className?: string }) {
  const { t } = useTranslation()
  return (
    <ol
      aria-label={t('planner.step', { current: 1, total: PILLS.length })}
      className={cn(
        'flex flex-wrap items-center justify-center gap-1.5 text-[12.5px] font-semibold',
        className,
      )}
    >
      {PILLS.map((key, i) => (
        <li
          key={key}
          aria-current={i === 0 ? 'step' : undefined}
          className={cn(
            'rounded-full px-[11px] py-[5px]',
            i === 0 ? 'bg-primary/15 text-primary-text' : 'bg-muted text-muted-foreground',
          )}
        >
          {i + 1} · {t(`planner.pills.${key}`)}
        </li>
      ))}
    </ol>
  )
}

export function TryChips({ onPick }: { onPick: (text: string) => void }) {
  const { t } = useTranslation()
  const examples = t('planner.tryExamples', { returnObjects: true }) as string[]
  if (!Array.isArray(examples)) return null
  return (
    <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
      <span className="font-semibold">{t('planner.try')}</span>
      {examples.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => onPick(e)}
          className="cursor-pointer rounded-full border border-dashed border-border px-[11px] py-1.5 text-foreground/80 transition-colors hover:border-foreground/30 hover:bg-muted/60 hover:text-foreground"
        >
          {e}
        </button>
      ))}
    </div>
  )
}

const QUESTION_KEYS = ['level', 'budget', 'deadline'] as const

// The one goal the new-goal flow keeps in progress (persisted in localStorage).
export function useGoalDraft() {
  const goalText = useOnboardingStore((s) => s.goalText)
  const tree = useOnboardingStore((s) => s.generatedTree)
  const answers = useOnboardingStore((s) => s.planAnswers)
  if (tree) return { kind: 'plan' as const, title: tree.title }
  if (goalText.trim())
    return {
      kind: 'draft' as const,
      title: goalText.trim(),
      answered: QUESTION_KEYS.filter((k) => answers[k] != null).length,
    }
  return null
}

export function DraftsPanel({
  draft,
  onResume,
}: {
  draft: NonNullable<ReturnType<typeof useGoalDraft>>
  onResume: () => void
}) {
  const { t } = useTranslation()
  return (
    <aside className="flex min-w-0 flex-[1_1_280px] flex-col gap-2.5 self-start rounded-[20px] border border-border bg-card/70 p-4 backdrop-blur-sm">
      <h2 className="text-[11px] font-extrabold tracking-[0.12em] text-muted-foreground uppercase">
        {t('planner.drafts.title')}
      </h2>
      <div className="relative">
        <button
          type="button"
          onClick={onResume}
          className="flex w-full cursor-pointer flex-col gap-[3px] rounded-xl bg-muted/60 p-2.5 pr-9 text-left transition-colors hover:bg-muted"
        >
          <span className="line-clamp-2 text-sm font-bold text-foreground">{draft.title}</span>
          <span className="text-xs text-muted-foreground">
            {draft.kind === 'plan'
              ? t('planner.drafts.planReady')
              : draft.answered
                ? t('planner.drafts.answered', { count: draft.answered, total: 3 })
                : t('planner.drafts.notStarted')}
          </span>
        </button>
        <button
          type="button"
          onClick={() => useOnboardingStore.getState().reset()}
          aria-label={t('planner.drafts.discard')}
          title={t('planner.drafts.discard')}
          className="absolute top-2 right-2 flex size-6 cursor-pointer items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:bg-background hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </div>
    </aside>
  )
}

function whyLine(
  t: (k: string, o?: Record<string, unknown>) => string,
  f: GoalIdeaFact,
  lang: string,
) {
  switch (f.kind) {
    case 'next':
      return t('planner.ideas.why.next', { title: f.title })
    case 'habit':
      return t('planner.ideas.why.habit', { title: f.title, pct: f.pct })
    case 'streak':
      return t('planner.ideas.why.streak', { title: f.title, count: f.days })
    case 'builds':
      return t('planner.ideas.why.builds', { title: f.title })
    case 'area': {
      const area = IMPROVE_AREAS.find((a) => a.key === f.area)
      return t('planner.ideas.why.area', { area: area?.label[improveLang(lang)] ?? f.area })
    }
  }
}

export function GoalIdeas({ onPick }: { onPick: (goal: string) => void }) {
  const { i18n } = useTranslation()
  const ideas = useQuery(api.goalIdeas.mine, {})
  const request = useMutation(api.goalIdeas.request)
  const requestedRef = useRef(false)
  useEffect(() => {
    if (ideas !== null || requestedRef.current) return
    requestedRef.current = true
    void request({ language: i18n.language }).catch(() => {})
  }, [ideas, request, i18n.language])
  if (ideas && !ideas.pending && ideas.ideas.length === 0) return null
  return <GoalIdeasView ideas={ideas && !ideas.pending ? ideas.ideas : null} onPick={onPick} />
}

export function GoalIdeasView({
  ideas,
  onPick,
}: {
  ideas: { goal: string; category: string; fact: GoalIdeaFact }[] | null
  onPick: (goal: string) => void
}) {
  const { t, i18n } = useTranslation()
  return (
    <section className="flex w-full flex-col gap-3">
      <div className="flex flex-col gap-[3px]">
        <h2 className="text-lg font-extrabold tracking-tight">{t('planner.ideas.title')}</h2>
        <p className="text-[13px] text-muted-foreground">{t('planner.ideas.subtitle')}</p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,280px),1fr))] gap-3">
        {!ideas
          ? [62, 78, 54].map((w) => (
              <div
                key={w}
                className="flex flex-col gap-2 rounded-2xl border border-border bg-card p-3.5"
              >
                <div className="h-2.5 w-40 animate-pulse rounded bg-muted" />
                <div className="h-4 animate-pulse rounded bg-muted" style={{ width: `${w}%` }} />
                <div className="h-3 w-24 animate-pulse rounded bg-muted/60" />
              </div>
            ))
          : ideas.map((idea) => {
              const color = CATEGORY_COLORS[idea.category] ?? CATEGORY_COLORS.mind
              return (
                <button
                  key={idea.goal}
                  type="button"
                  onClick={() => onPick(idea.goal)}
                  className="group flex cursor-pointer flex-col gap-1.5 rounded-2xl border border-border bg-card p-3.5 text-left transition-colors hover:border-foreground/20"
                >
                  <span
                    className="text-[11px] font-bold"
                    style={{ color: categoryTextColor(color) }}
                  >
                    {whyLine(t, idea.fact, i18n.language)}
                  </span>
                  <span className="text-[15.5px] leading-tight font-extrabold text-foreground">
                    {idea.goal}
                  </span>
                  <span className="mt-auto flex items-center gap-1 pt-0.5 text-[12.5px] font-bold text-primary-text">
                    {t('planner.ideas.use')}
                    <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </button>
              )
            })}
      </div>
    </section>
  )
}
